import { BookOpen, Code, FlaskConical, Globe, Zap, Video, type LucideIcon } from "lucide-react";
import type { OfferingType } from "@/services/offeringSearchService";

export type CardVisual = { icon: LucideIcon; gradient: string; pattern: "radial" | "dots" | "grid" };

// Navy-to-teal — GROUP (matches logo colors)
const GROUP_CARD_VISUALS: CardVisual[] = [
  { icon: BookOpen,     gradient: "from-[hsl(210,60%,18%)] via-[hsl(195,58%,28%)] to-[hsl(173,55%,38%)]", pattern: "radial" },
  { icon: Globe,        gradient: "from-[hsl(210,60%,22%)] via-[hsl(190,56%,30%)] to-[hsl(173,52%,40%)]", pattern: "dots"  },
  { icon: Code,         gradient: "from-[hsl(210,60%,16%)] via-[hsl(198,58%,26%)] to-[hsl(173,55%,36%)]", pattern: "grid"  },
  { icon: FlaskConical, gradient: "from-[hsl(210,60%,20%)] via-[hsl(192,57%,29%)] to-[hsl(173,54%,39%)]", pattern: "radial" },
];

// Dark navy — CRASH_COURSE
const CRASH_CARD_VISUALS: CardVisual[] = [
  { icon: Zap,          gradient: "from-[hsl(210,60%,12%)] via-[hsl(212,62%,18%)] to-[hsl(214,58%,26%)]", pattern: "radial" },
  { icon: FlaskConical, gradient: "from-[hsl(210,60%,10%)] via-[hsl(212,60%,16%)] to-[hsl(215,58%,24%)]", pattern: "dots"  },
  { icon: BookOpen,     gradient: "from-[hsl(210,60%,14%)] via-[hsl(213,62%,20%)] to-[hsl(215,58%,28%)]", pattern: "grid"  },
  { icon: Code,         gradient: "from-[hsl(210,60%,11%)] via-[hsl(212,61%,17%)] to-[hsl(214,58%,25%)]", pattern: "radial" },
];

// Indigo/violet — ONE_TO_ONE
const ONE_TO_ONE_CARD_VISUALS: CardVisual[] = [
  { icon: Video,        gradient: "from-[hsl(243,50%,24%)] via-[hsl(232,52%,34%)] to-[hsl(210,60%,40%)]", pattern: "radial" },
  { icon: BookOpen,     gradient: "from-[hsl(243,50%,22%)] via-[hsl(230,52%,32%)] to-[hsl(210,60%,38%)]", pattern: "dots"  },
  { icon: Globe,        gradient: "from-[hsl(243,50%,26%)] via-[hsl(233,52%,36%)] to-[hsl(210,60%,42%)]", pattern: "grid"  },
  { icon: Code,         gradient: "from-[hsl(243,50%,20%)] via-[hsl(231,52%,30%)] to-[hsl(210,60%,36%)]", pattern: "radial" },
];

export function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash);
}

/** Deterministic visual per (subject, offering type) so the same offering always renders the same. */
export function getOfferingCardVisual(subject: string | null, offeringType: OfferingType): CardVisual {
  const palette = offeringType === "GROUP" ? GROUP_CARD_VISUALS
    : offeringType === "CRASH_COURSE" ? CRASH_CARD_VISUALS
    : ONE_TO_ONE_CARD_VISUALS;
  return palette[hashString(subject ?? offeringType) % palette.length];
}

export function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export const OFFERING_TYPE_LABELS: Record<OfferingType, string> = {
  ONE_TO_ONE: "1-on-1",
  GROUP: "Group",
  CRASH_COURSE: "Crash Course",
};
